import { useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";

import { DrawerMenu } from "components/DrawerMenu";
import { DashboardBackButton } from "components/Shipment/components/DashboardBackButton";
import { useDisclosure } from "hooks";
import { useIsMobile } from "hooks/useIsMobile";
import { PageLayout } from "layouts";

import { OpenDrawerMenuButton } from "../OpenDrawerMenuButton";
import { ShipmentSideBar } from "../ShipmentSideBar";

interface Props {
  shipmentOverview: React.ReactNode;
  shipments: CarrierCondensedShipment[];
  shipmentSelectionIsDisabled: boolean;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  page: number;
  pages: number;
  shipmentCount: number;
  onFiltersClick?: VoidFunction;
}

export function Shipment(props: Props) {
  const {
    shipmentOverview,
    shipments,
    shipmentSelectionIsDisabled,
    handlePageSelected,
    page,
    pages,
    shipmentCount,
    onFiltersClick,
  } = props;

  const showFullShipmentSideBar = !useIsMobile({ maxWidth: 1200 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure(false);

  const shipmentBrowser = (
    <ShipmentSideBar
      shipments={shipments}
      shipmentSelectionIsDisabled={shipmentSelectionIsDisabled}
      page={page}
      pages={pages}
      handlePageSelected={handlePageSelected}
      shipmentCount={shipmentCount}
      showDashboardBackButton={showFullShipmentSideBar}
      onFiltersClick={onFiltersClick}
      onCollapse={
        showFullShipmentSideBar ? () => setSidebarCollapsed(true) : undefined
      }
    />
  );

  return (
    <PageLayout>
      {!showFullShipmentSideBar && (
        <Stack direction="column">
          <DashboardBackButton />
          <OpenDrawerMenuButton onOpen={onOpen} buttonLabel="SHIPMENT LIST" />
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={showFullShipmentSideBar ? 2 : undefined}
        sx={{ alignItems: "flex-start" }}
      >
        {showFullShipmentSideBar && (
          <Stack
            sx={{
              width: sidebarCollapsed ? 48 : "25%",
              minWidth: sidebarCollapsed ? 48 : 280,
              position: "sticky",
              top: 0,
              transition: "width 0.3s ease-in-out, min-width 0.3s ease-in-out",
            }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Expand button - visible when collapsed */}
              <Box
                sx={{
                  position: sidebarCollapsed ? "relative" : "absolute",
                  opacity: sidebarCollapsed ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                  pointerEvents: sidebarCollapsed ? "auto" : "none",
                  zIndex: 1,
                }}
              >
                <Tooltip title="Show shipment list">
                  <IconButton
                    onClick={() => setSidebarCollapsed(false)}
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      "&:hover": {
                        backgroundColor: "grey.100",
                      },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Sidebar content - visible when expanded */}
              <Box
                sx={{
                  opacity: sidebarCollapsed ? 0 : 1,
                  transition: "opacity 0.3s ease-in-out",
                  pointerEvents: sidebarCollapsed ? "none" : "auto",
                  position: sidebarCollapsed ? "absolute" : "relative",
                  width: "100%",
                }}
              >
                {shipmentBrowser}
              </Box>
            </Box>
          </Stack>
        )}
        <Stack spacing={showFullShipmentSideBar ? 2 : undefined} flex={1}>
          {!showFullShipmentSideBar && (
            <DrawerMenu isOpen={isOpen} onClose={onClose}>
              {shipmentBrowser}
            </DrawerMenu>
          )}
          {shipmentOverview}
        </Stack>
      </Stack>
    </PageLayout>
  );
}
